import {
  Box,
  Button,
  Center,
  Flex,
  HStack,
  Heading,
  Link,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Tag,
  Text,
  Tooltip,
  VStack,
} from "@chakra-ui/react";
import { ColumnDef } from "@tanstack/react-table";
import axios from "axios";
import Papa from "papaparse";
import { useCallback, useEffect, useMemo, useState } from "react";
import { FiChevronDown, FiClock, FiDownload, FiX } from "react-icons/fi";
import { RiLogoutBoxRLine } from "react-icons/ri";
import { useNavigate, useParams } from "react-router-dom";
import { OrganizationDateView } from "src/components/DataDisplay/OrganizationDateView";
import {
  StatusTag,
  StatusTagMetadata,
} from "src/components/DataDisplay/StatusTag";
import { ProgressMeter } from "src/components/Feedback/ProgressMeter";
import { GQLRemoteDataView } from "src/components/Layout/RemoteDataView";
import { Breadcrumb } from "src/components/Navigation/Breadcrumb";
import { useFlags } from "src/components/Providers/FeatureFlagProvider";
import { CustomColumnsTable } from "src/components/Table/CustomColumnsTable";
import { SortType } from "src/components/Table/SortButton";
import { HighlightedText } from "src/components/Text/HighlightedText";
import { TextWithOverflowTooltip } from "src/components/TextWithOverflowTooltip";
import {
  DocumentStorageStatus,
  useDocumentStorage,
} from "src/hooks/useDocumentStorage";
import { useDownloadProgress } from "src/hooks/useDownload";
import { useOrganization } from "src/hooks/useOrganization";
import { useRemoteDataQuery } from "src/hooks/useRemoteDataQuery";
import { useSchoolAdmin } from "src/hooks/useSchoolAdmin";
import { GET_MATCH_DETAILS } from "src/scenes/orgAdmin/match/graphql/queries";
import * as breadcrumb from "src/services/breadcrumb";
import { getFormIdFromRow, triggerDownload } from "src/services/dataTransfer";
import * as Url from "src/services/url";
import * as GQL from "src/types/graphql";
import { PublishInfo } from "./components/PublishInfo";
import { PublishLotteryPositionButton } from "./components/PublishLotteryPositionButton";
import { ShowPublicViewButton } from "./components/ShowPublicViewButton";

// Define the placement status metadata
const placementStatuses: Record<ResultStatus, StatusTagMetadata> = {
  Offer: {
    label: "Offered",
    longLabel: "Offered",
    icon: RiLogoutBoxRLine,
    colorScheme: "blue",
  },
  Waitlist: {
    label: "Waitlisted",
    longLabel: "Waitlisted",
    icon: FiClock,
    colorScheme: "orange",
  },
  "Not eligible - No schools selected": {
    label: "Not eligible",
    longLabel: "Not eligible",
    icon: FiX,
    colorScheme: "gray",
  },
};

// Define the data structure for match details
interface MatchDetail {
  formId: string;
  student: {
    name: string;
    id: string;
  };
  school: string;
  result: keyof typeof placementStatuses;
  grade: string;
  lotteryNumber: string;
  lotteryNumbersDrawn?: number;
  priorityGroup: string;
  tagGroup?: string;
  tags: string;
  lotteryPosition: string;
  calculatedWaitlistPosition: string;
}

export enum ResultStatus {
  Offer = "Offer",
  Waitlist = "Waitlist",
  NotEligible = "Not eligible - No schools selected",
}

interface MatchResultRow {
  "App ID"?: string;
  "Form ID"?: string;
  "Student First Name": string;
  "Student Last Name": string;
  "Student ID": string;
  School: string;
  "School ID": string;
  Result: ResultStatus;
  Grade: string;
  "Lottery Number": string;
  "Lottery Numbers Drawn"?: string;
  "Priority Group": string;
  "Tag Group"?: string;
  Tags: string;
  "Lottery Position": string;
  "Calculated Waitlist Position": string;
}

export const Details: React.FC = () => {
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(25);
  const organization = useOrganization();
  const navigate = useNavigate();
  const flags = useFlags(["lottery-position"]);
  const { matchId } = useParams<{ matchId: string }>();
  const [matchResults, setMatchResults] = useState<MatchDetail[]>([]);
  const [isLoadingResults, setIsLoadingResults] = useState(false);
  const [sortField, setSortField] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<SortType>("unsorted");
  const [csvText, setCsvText] = useState<string>("");
  const [configText, setConfigText] = useState<string>("");
  const [capacitiesCsvText, setCapacitiesCsvText] = useState<string>("");
  const { isSchoolAdmin } = useSchoolAdmin();
  const { downloadProgress, onDownloadProgress, resetDownloadProgress } =
    useDownloadProgress();

  const { getDownloadUrl } = useDocumentStorage();
  const { remoteData: matchData } = useRemoteDataQuery<
    GQL.GetMatchDetails,
    GQL.GetMatchDetailsVariables
  >(GET_MATCH_DETAILS, {
    variables: { matchId: matchId ?? "" },
    skip: !matchId,
    fetchPolicy: "cache-and-network",
  });

  // Fetch and parse CSV when match data is loaded
  useEffect(() => {
    async function fetchResults() {
      if (!matchData.hasData()) return;
      const documentId = matchData.data.match_run_by_pk?.results_document_id;
      if (!documentId) return;

      setIsLoadingResults(true);
      resetDownloadProgress();
      try {
        const documentUrl = await getDownloadUrl(documentId);
        if (documentUrl.status !== DocumentStorageStatus.OK) return;

        const response = await axios.get(documentUrl.data, {
          onDownloadProgress,
        });
        let csvText = response.data as string;

        // Rename "Lottery Result" to "Result"
        const rows = Papa.parse(csvText, {
          header: true,
          transformHeader: (h) => (h === "Lottery Result" ? "Result" : h),
        });

        // For school admins, remove the Rank column from the CSV
        if (isSchoolAdmin) {
          if (rows.data && rows.data.length > 0) {
            // Remove Rank field from each row
            const filteredData = rows.data.map((row: any) => {
              const { Rank, ...rest } = row;
              return rest;
            });

            // Generate new CSV without Rank column
            csvText = Papa.unparse(filteredData);
          }
        } else {
          csvText = Papa.unparse(rows.data);
        }

        setCsvText(csvText);

        Papa.parse<MatchResultRow>(csvText, {
          header: true,
          complete: (results) => {
            const parsedResults = results.data
              .filter((row) => getFormIdFromRow(row)) // Filter out empty rows
              .map((row) => ({
                formId: getFormIdFromRow(row) ?? "",
                student: {
                  name: `${row["Student First Name"]} ${row["Student Last Name"]}`,
                  id: row["Student ID"],
                },
                school: row["School"],
                result: row["Result"],
                grade: row["Grade"],
                lotteryNumber: row["Lottery Number"],
                lotteryNumbersDrawn:
                  row["Lottery Numbers Drawn"]?.split(",").length,
                priorityGroup: row["Priority Group"],
                tagGroup: row["Tag Group"],
                tags: row["Tags"],
                lotteryPosition: row["Lottery Position"],
                calculatedWaitlistPosition: row["Calculated Waitlist Position"],
              }));

            setMatchResults(parsedResults);
          },
          error: (error: any) => {
            console.error("CSV Parse Error:", error);
            setMatchResults([]);
            setCsvText("");
          },
        });
      } catch (error) {
        console.error("Error fetching results:", error);
        setMatchResults([]);
        setCsvText("");
      } finally {
        setIsLoadingResults(false);
      }
    }

    fetchResults();
  }, [
    matchData,
    getDownloadUrl,
    onDownloadProgress,
    resetDownloadProgress,
    isSchoolAdmin,
  ]);

  const usedMultipleLotteryNumbers = useMemo(() => {
    // Check the config for priority tag groups with multiple lottery numbers.
    for (const m of configText.matchAll(/"lotteryNumbers"\s*:\s*(\d+)/g)) {
      if (Number(m[1]) > 1) return true;
    }
    // Check the results for any records with multiple lottery numbers drawn.
    return matchResults.some(
      (result) => result.lotteryNumbersDrawn && result.lotteryNumbersDrawn > 1
    );
  }, [matchResults, configText]);

  // Fetch capacity snapshot when match data is loaded
  useEffect(() => {
    async function fetchCapacities() {
      if (!matchData.hasData()) return;
      const documentId =
        matchData.data.match_run_by_pk?.capacities_snapshot_document_id;
      if (!documentId) return;

      try {
        const documentUrl = await getDownloadUrl(documentId);
        if (documentUrl.status !== DocumentStorageStatus.OK) return;

        const response = await fetch(documentUrl.data);
        const capacitiesCsvText = await response.text();
        setCapacitiesCsvText(capacitiesCsvText);
      } catch (error) {
        console.error("Error fetching capacities:", error);
        setCapacitiesCsvText("");
      }
    }

    fetchCapacities();
  }, [matchData, getDownloadUrl]);

  // Fetch config when match data is loaded
  useEffect(() => {
    async function fetchConfig() {
      if (!matchData.hasData()) return;
      const documentId =
        matchData.data.match_run_by_pk?.match_config_snapshot_document_id;
      if (!documentId) return;

      try {
        const documentUrl = await getDownloadUrl(documentId);
        if (documentUrl.status !== DocumentStorageStatus.OK) return;

        const response = await fetch(documentUrl.data);
        const configText = await response.text();
        setConfigText(configText);
      } catch (error) {
        console.error("Error fetching config:", error);
        setConfigText("");
      }
    }

    fetchConfig();
  }, [matchData, getDownloadUrl]);

  const handleChangeSort = useCallback(
    (headerId: string, sortType: SortType) => {
      setSortField(headerId);
      setSortDirection(sortType);
    },
    []
  );

  const handleFetchMore = useCallback((limit: number, offset: number) => {
    setPageSize(limit);
    setCurrentPage(Math.floor(offset / limit) + 1);
  }, []);

  const matchName = matchData
    .map((data) => data.match_run_by_pk?.name ?? "Match")
    .withDefault("Match");
  const handleDownload = useCallback(
    async (type: "results" | "capacities" | "rules") => {
      switch (type) {
        case "results":
          if (csvText) {
            const blob = new Blob([csvText], { type: "text/csv" });
            triggerDownload(blob, `${matchName}-results.csv`);
          }
          break;
        case "capacities":
          if (capacitiesCsvText) {
            const blob = new Blob([capacitiesCsvText], { type: "text/csv" });
            triggerDownload(blob, `${matchName}-capacities.csv`);
          }
          break;
        case "rules":
          if (configText) {
            const blob = new Blob([configText], { type: "application/json" });
            triggerDownload(blob, `${matchName}-rules.json`);
          }
          break;
      }
    },
    [csvText, configText, capacitiesCsvText, matchName]
  );

  // Sort and paginate the data
  const sortedAndPaginatedData = useMemo(() => {
    const sortedResults = [...matchResults];

    if (sortField && sortDirection !== "unsorted") {
      sortedResults.sort((a, b) => {
        let aValue: string;
        let bValue: string;

        switch (sortField) {
          case "student":
            aValue = a.student.name.toLowerCase();
            bValue = b.student.name.toLowerCase();
            break;
          case "school":
            aValue = a.school.toLowerCase();
            bValue = b.school.toLowerCase();
            break;
          default:
            return 0;
        }

        if (sortDirection === "asc") {
          return aValue.localeCompare(bValue);
        } else {
          return bValue.localeCompare(aValue);
        }
      });
    }

    // Paginate the sorted results
    const start = (currentPage - 1) * pageSize;
    const end = start + pageSize;
    return sortedResults.slice(start, end);
  }, [matchResults, sortField, sortDirection, currentPage, pageSize]);

  return (
    <GQLRemoteDataView remoteData={matchData}>
      {(data) => {
        const matchName = data.match_run_by_pk?.name ?? "Match";
        const columns: ColumnDef<MatchDetail>[] = [
          {
            id: "student",
            header: "Student",
            accessorKey: "student",
            cell: ({ row }) => {
              const student = row.original.student;

              const url = Url.OrgAdmin.Students.edit(organization, student.id);
              return (
                <Box>
                  <Link
                    href={url}
                    color="primary.500"
                    textDecoration="underline"
                  >
                    {student.name}
                  </Link>
                  <Text fontSize="sm" color="gray.500">
                    {student.id}
                  </Text>
                </Box>
              );
            },
          },
          {
            id: "result",
            header: "Result",
            accessorKey: "result",
            cell: ({ row }) => {
              return (
                <StatusTag status={placementStatuses[row.original.result]!} />
              );
            },
          },
          {
            id: "school",
            header: "School",
            accessorKey: "school",
            cell: ({ row }) => <Text fontSize="sm">{row.original.school}</Text>,
          },
          {
            id: "grade",
            header: "Grade",
            accessorKey: "grade",
          },
          {
            id: "priorityGroup",
            header: "Priority Group",
            cell: ({ row }) => {
              const tagGroup =
                row.original.tagGroup !== undefined
                  ? `(${row.original.tagGroup})`
                  : "";
              return (
                <Text
                  fontSize="sm"
                  color={
                    tagGroup && row.original.priorityGroup.includes(tagGroup)
                      ? "gray.500"
                      : "inherit"
                  }
                >
                  <HighlightedText
                    text={row.original.priorityGroup}
                    keyword={tagGroup}
                    highlightProps={{ color: "gray.800" }}
                  />
                </Text>
              );
            },
          },
          ...((usedMultipleLotteryNumbers
            ? [
                {
                  id: "lotteryNumbersDrawn",
                  header: "Lottery Numbers Drawn",
                  accessorFn: (row) => row.lotteryNumbersDrawn,
                },
              ]
            : []) satisfies ColumnDef<MatchDetail>[]),
          {
            id: "lotteryNumber",
            header: "Lottery Number",
            accessorKey: "lotteryNumber",
          },
          {
            id: "lotteryPosition",
            header: "Lottery Position",
            accessorKey: "lotteryPosition",
          },
          {
            id: "calculatedWaitlistPosition",
            header: "Calculated Waitlist Position",
            accessorKey: "calculatedWaitlistPosition",
          },
          {
            id: "tags",
            header: "Tags",
            accessorKey: "tags",
            cell: ({ row }) => {
              const tags = row.original.tags.split(",");
              if (tags.length > 1) {
                return (
                  <Tooltip hasArrow label={row.original.tags}>
                    <HStack tabIndex={0} width="fit-content">
                      <Tag variant="tag">
                        <Text
                          overflow="hidden"
                          textOverflow="ellipsis"
                          maxWidth="300px"
                        >
                          {tags[0]}
                        </Text>
                      </Tag>
                      <Tag variant="tag">+{tags.length - 1}</Tag>
                    </HStack>
                  </Tooltip>
                );
              }
              if (tags[0]) {
                return (
                  <Tag variant="tag">
                    <TextWithOverflowTooltip
                      maxWidth="300px"
                      content={tags[0] ?? ""}
                      hasArrow
                    />
                  </Tag>
                );
              }
              return <></>;
            },
          },
          {
            id: "formId",
            header: "Form ID",
            accessorKey: "formId",
            cell: ({ row }) => {
              const formId = row.original.formId;
              return (
                <Link
                  fontSize="sm"
                  color="blue.600"
                  onClick={() => {
                    navigate(
                      Url.OrgAdmin.Forms.view({
                        organization,
                        formTemplateId: "x", // Saves a query to find the form template
                        id: formId,
                      })
                    );
                  }}
                >
                  {formId}
                </Link>
              );
            },
          },
        ];

        return (
          <VStack align="stretch" spacing={6} width="100%" height="80%">
            <Breadcrumb
              items={breadcrumb.match.getBreadcrumbsForDetails(
                organization,
                matchId ?? ""
              )}
            />
            <HStack justify="space-between" align="flex-start" width="100%">
              <VStack align="flex-start" spacing={1}>
                <Text color="gray.600">Results for</Text>
                <Heading as="h1" size="lg" textAlign="left">
                  {matchName}
                </Heading>
                <Text color="gray.500" fontSize="md">
                  Run on{" "}
                  <OrganizationDateView
                    isoDate={data.match_run_by_pk?.created_at}
                  />
                </Text>
              </VStack>

              <VStack align="end">
                <HStack spacing={2}>
                  <Menu>
                    <MenuButton
                      as={Button}
                      leftIcon={<FiDownload />}
                      rightIcon={<FiChevronDown />}
                      variant="ghost"
                      isDisabled={!csvText && !capacitiesCsvText && !configText}
                    >
                      Download
                    </MenuButton>
                    <MenuList>
                      <MenuItem
                        onClick={() => handleDownload("results")}
                        isDisabled={!csvText}
                      >
                        Results as .CSV
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleDownload("capacities")}
                        isDisabled={!capacitiesCsvText}
                      >
                        Capacities as .CSV
                      </MenuItem>
                      <MenuItem
                        onClick={() => handleDownload("rules")}
                        isDisabled={!configText}
                      >
                        Rules as .JSON
                      </MenuItem>
                    </MenuList>
                  </Menu>

                  <ShowPublicViewButton id={matchId ?? ""} />
                  {flags["lottery-position"].enabled && (
                    <PublishLotteryPositionButton matchId={matchId ?? ""} />
                  )}
                </HStack>
                {flags["lottery-position"].enabled && (
                  <PublishInfo matchId={matchId ?? ""} />
                )}
              </VStack>
            </HStack>

            {matchData.isLoading() || isLoadingResults ? (
              <Center h="100%">
                <Flex
                  direction="column"
                  alignItems="center"
                  gap={2}
                  width="100%"
                >
                  <Text>Downloading results...</Text>
                  <ProgressMeter
                    value={downloadProgress * 100}
                    size="lg"
                    maxWidth="400px"
                  />
                </Flex>
              </Center>
            ) : (
              <CustomColumnsTable<MatchDetail>
                data={sortedAndPaginatedData}
                columns={columns}
                isLoading={false}
                resourceName="match_results"
                sortableColumnIds={["student", "school"]}
                onChangeSort={handleChangeSort}
                count={matchResults.length}
                offset={(currentPage - 1) * pageSize}
                limit={pageSize}
                onFetchMore={handleFetchMore}
              />
            )}
          </VStack>
        );
      }}
    </GQLRemoteDataView>
  );
};
